import type { RotaryKilnLoadResult } from '../../../types/reports.js';
import { formatNumber } from '../../../utils/formatting.js';

/**
 * Anzeigerundung der Frachtenberechnung (Phase 38, D-18/Sketch 021 A+, `index.html` Zeilen
 * 436-447). Reine Modul-Datei, kein React-Import -- Muster `kilnFields.ts`.
 *
 * Alle drei Funktionen sind ausschliesslich Anzeige-Rundung: der Zahlenwert selbst kommt fertig
 * gerechnet vom Server (D-07 -- die Fracht entsteht aus dem ungerundeten Zwischenwert `volume`,
 * nie aus dem gerundeten `weightedFlow`), hier wird nichts nachgerechnet. `null`/leer ergibt
 * ueberall das Gedankenstrich-Zeichen '—' (nicht formatNumber()s eigenes '-': dasselbe
 * "keine Zahl vorhanden"-Zeichen wie formatTonnage(), Konvention dieses Moduls).
 */

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(num) ? null : num;
}

function formatFixed(value: string | number | null | undefined, decimals: number): string {
  const num = toNumber(value);
  return num === null ? '—' : formatNumber(num, decimals);
}

/**
 * Frachten (Sketch-Funktion `dyn`, `index.html:442`): Betrag ab 100 keine Nachkommastelle, ab 10
 * eine, ab 1 zwei, darunter drei -- eine grosse Fracht (z.B. Staub, kg/a) und eine winzige
 * (z.B. PCDD/F, mg TE/a) bleiben beide lesbar, ohne dass die kleine auf '0' rundet.
 */
export function formatLoadValue(value: string | number | null | undefined): string {
  const num = toNumber(value);
  if (num === null) return '—';
  const abs = Math.abs(num);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : 3;
  return formatNumber(num, decimals);
}

/**
 * Gewichteter Volumenstrom (Feld 4) -- der Sketch zeigt ihn OHNE Nachkommastelle
 * (`z(r.weighted)`, `index.html:519`, `z` faellt auf `d = 0` zurueck), nicht mit der gestuften
 * Frachten-Rundung von formatLoadValue(). Eigene Funktion statt Wiederverwendung, damit eine
 * spaetere Anpassung der Frachten-Stufung dieses Feld nicht versehentlich mitaendert.
 */
export function formatFlowValue(value: string | number | null | undefined): string {
  return formatFixed(value, 0);
}

/** Gesamtlaufzeit -- ebenfalls ohne Nachkommastelle (Sketch `z(r.hours)`, `index.html:518`). */
export function formatHoursValue(value: string | number | null | undefined): string {
  return formatFixed(value, 0);
}

/**
 * Einzige Stelle der "liegt ein Ergebnis vor"-Pruefung (Plan 38.1-01, Sketch 022 D): true, wenn
 * der Server einen gewichteten Volumenstrom geliefert hat. Ersetzt die bislang in
 * `KilnLoadCalculationForm.tsx` inline gefuehrte Pruefung -- Plan 38.1-02 braucht dieselbe Frage
 * fuer den Kopfzaehler und soll sie nicht ein zweites Mal aufschreiben. Keine Rechnung, nur die
 * Frage, ob der Server ein Ergebnis mitgibt.
 */
export function hasWeightedFlow(result: RotaryKilnLoadResult | null | undefined): boolean {
  return result?.weightedFlow !== null && result?.weightedFlow !== undefined && result?.weightedFlow !== '';
}

/**
 * Herkunftsdatum (D-24, Sketch 024 `originLine`): `2026-09-12T10:00:00+02:00` -> `12.09.2026`.
 * Eigene, kleine Funktion statt Wiederverwendung eines der beiden bestehenden, modul-lokalen
 * Datumsformatierer in `pages/emissionReports/` (keine davon exportiert) -- `acceptedAt` traegt
 * Zeitzoneninformation, `Europe/Berlin` fixiert die Anzeige unabhaengig von der Browser-Zeitzone
 * des Melders.
 */
export function formatOriginDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  }).format(new Date(iso));
}

/** Zustand -> Anzeigename, D-22/D-23 (Sketch `fehlenderZustand`/`fracht`). */
const MISSING_STATE_LABELS: Record<'compound' | 'direct', string> = {
  compound: 'Verbundbetrieb',
  direct: 'Direktbetrieb',
};

function otherMissingState(state: 'compound' | 'direct'): 'compound' | 'direct' {
  return state === 'compound' ? 'direct' : 'compound';
}

/**
 * Satz-Wortlaut D-22 (fehlt ein Betriebszustand bei ALLEN Komponenten mit Werten): Vorbild ist
 * der Sketch (`index.html` Zeile 629), hier serverseitig entschieden -- `missingState` und
 * `coveredHours` kommen ausschliesslich aus `result.massFlowCoverage`, keine Neuberechnung im
 * Browser.
 */
export function missingStateSentence(
  missingState: 'compound' | 'direct',
  coveredHours: string | number | null | undefined,
): string {
  const missingLabel = MISSING_STATE_LABELS[missingState];
  const coveredLabel = MISSING_STATE_LABELS[otherMissingState(missingState)];
  return `${missingLabel} im Bericht nicht gemessen. Die Frachten umfassen nur den ${coveredLabel} (${formatHoursValue(coveredHours)} h).`;
}

/**
 * Tooltip-Wortlaut D-23 (Einzelhinweis je Komponente, `LabelWithInfo`): dieselben Zustandsnamen
 * wie `missingStateSentence`, Singularform ("Die Fracht" statt "Die Frachten") -- Sketch
 * `index.html` Zeile 636/637.
 */
export function missingStateTooltip(
  missingState: 'compound' | 'direct',
  coveredHours: string | number | null | undefined,
): string {
  const missingLabel = MISSING_STATE_LABELS[missingState];
  const coveredLabel = MISSING_STATE_LABELS[otherMissingState(missingState)];
  return `${missingLabel} nicht gemessen. Die Fracht umfasst nur den ${coveredLabel} (${formatHoursValue(coveredHours)} h).`;
}
